'use client'

import { AnimatePresence, motion, useWillChange } from 'motion/react'
import { memo, ReactNode, useCallback, useId, useMemo } from 'react'
import { prepareTagSlugs } from '@/api/restaurants/get-restaurants-query'
import { ITag } from '@/api/restaurants/types/restaurant-types'
import { useGetAllTagsQuery } from '@/api/tags/get-all-tags-query'
import { useRestaurantCitySearchContext } from '@/features/restaurants/restaurants-city-search/utils/restaurant-city-search-context'

interface IRenderTagProps {
    toggleTag: () => void
    isLast: boolean
}

export type IRestaurantDisplayTagsRenderTag = (tag: ITag, props: IRenderTagProps) => ReactNode

interface IRestaurantCitySearchFiltersDesktopDisplayTagsProps {
    type: 'active' | 'inactive'
    renderTag: IRestaurantDisplayTagsRenderTag
}

export const RestaurantCitySearchFiltersDisplayTagsDesktop = memo(
    ({ type, renderTag }: IRestaurantCitySearchFiltersDesktopDisplayTagsProps) => {
        const componentId = useId()

        const willChange = useWillChange()

        const {
            query,
            setters: { setTagSlugs },
        } = useRestaurantCitySearchContext()

        const { data } = useGetAllTagsQuery(query.citySlug ?? undefined)

        const allTags = data?.data

        const tagsToDisplay = useMemo(
            () =>
                allTags?.filter((tag) => {
                    const isTagSelected = query.tagSlugs?.includes(tag.slug)

                    return type === 'active' ? isTagSelected : !isTagSelected
                }),
            [allTags, query.tagSlugs, type]
        )

        const toggleTag = useCallback(
            (tag: ITag) => {
                const prev = prepareTagSlugs(query.tagSlugs) ?? []

                const nextSlugs = prev.includes(tag.slug)
                    ? prev.filter((prevTag) => prevTag !== tag.slug)
                    : [...prev, tag.slug]

                setTagSlugs(nextSlugs)
            },
            [query.tagSlugs, setTagSlugs]
        )

        const tagNodes = useMemo(() => {
            return tagsToDisplay?.map((tag, index, arr) => {
                const isLast = index === arr.length - 1

                return (
                    <motion.div
                        style={{ willChange }}
                        key={tag.slug + 'restaurant-city-search-active-tag' + componentId}
                        layout="position"
                        layoutDependency={arr.length}
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0 }}
                        transition={{
                            layout: {
                                type: 'spring',
                                stiffness: 700,
                                damping: 50,
                            },
                            scale: {
                                type: 'spring',
                                stiffness: 700,
                                damping: 50,
                            },
                        }}
                        className="overflow-hidden origin-center flex flex-row min-w-fit"
                    >
                        <div
                            key={tag.slug + 'restaurant-city-search-tag' + componentId}
                            className="flex flex-row items-center select-none"
                        >
                            {renderTag(tag, { isLast, toggleTag: () => toggleTag(tag) })}
                        </div>
                    </motion.div>
                )
            })
        }, [componentId, renderTag, tagsToDisplay, toggleTag, willChange])

        return (
            <AnimatePresence mode="popLayout" initial={false}>
                {tagNodes}
            </AnimatePresence>
        )
    }
)

RestaurantCitySearchFiltersDisplayTagsDesktop.displayName = 'RestaurantCitySearchDisplayTags'
